import { useContext, useMemo, type CSSProperties } from 'react';
import { ImportedGroupContext, getImportedGroupCss } from './ImportedCodeModules';
import { importScope } from '../../Utils/importedHtml';
import { useTranslation } from 'react-i18next';
import { ModuleData } from '../../Types/containerV2';
import { isSegmentModuleContent } from '../../Types/containerV2/modules/segment';
import { useContainerContext } from './ContainerContext';
import ModuleRenderer from './ModuleRenderer';
import './SegmentModule.scss';

type SegmentModuleProps = {
	module: ModuleData;
};

const SegmentModule = ({ module }: SegmentModuleProps) => {
	const { t } = useTranslation();
	const { isSegmentExpanded, toggleSegment } = useContainerContext();
	const parentScope = useContext(ImportedGroupContext);
	const ownsScope = module.children.some((child) => child.moduleType === 'SharedCss');
	const scope = ownsScope ? importScope(module.guid) : parentScope?.scope;
	const importContext = useMemo(
		() =>
			ownsScope && scope
				? {
						scope,
						source: module.children
							.filter((child) => child.moduleType === 'SharedCss')
							.map((child) => String(child.content.sharedCss ?? ''))
							.join('\n'),
					}
				: parentScope,
		[ownsScope, scope, module.children, parentScope]
	);
	const groupCss = useMemo(
		() => (ownsScope && scope ? getImportedGroupCss(module.children, scope) : ''),
		[ownsScope, scope, module.children]
	);

	if (!isSegmentModuleContent(module.content)) {
		return null;
	}

	const { expandable, name } = module.content;

	const isExpandable = expandable.enabled;
	const isExpanded = isExpandable ? isSegmentExpanded(module.guid) : true;

	const handleToggle = () => {
		toggleSegment(module.guid);
	};

	const showExpandHeader = isExpandable && !expandable.completelyHidden;
	const showExpandButton = isExpandable && !expandable.previewAsButton && !expandable.completelyHidden;
	const segmentStyle = {
		'--biz-segment-gap': module.content.gap,
		'--biz-segment-mobile-gap': module.content.mobileGap ?? module.content.gap,
	} as CSSProperties;

	const classNames = ['biz-module', 'biz-segment-module'];
	if (ownsScope) classNames.push('biz-import-group', scope!);
	else if (parentScope) classNames.push('biz-import-section');
	if (isExpandable) classNames.push('biz-segment-module--expandable');
	if (isExpandable && isExpanded) classNames.push('biz-segment-module--expanded');
	if (module.content.columns && module.content.columns >= 2) {
		classNames.push('biz-segment-module--grid');
		classNames.push(`biz-segment-module--columns-${module.content.columns}`);
	}
	if (module.content.mobileColumns && module.content.mobileColumns >= 1) {
		classNames.push(`biz-segment-module--mobile-columns-${module.content.mobileColumns}`);
	}
	if (module.content.justifyContent) classNames.push(`biz-segment-module--justify-${module.content.justifyContent}`);
	if (module.content.alignItems) classNames.push(`biz-segment-module--align-${module.content.alignItems}`);

	return (
		<ImportedGroupContext.Provider value={importContext}>
			<div className={classNames.join(' ')} style={segmentStyle}>
				{groupCss && <style>{groupCss}</style>}
				{showExpandHeader && (
					<div className='biz-segment-module__expand-header' onClick={handleToggle}>
						{expandable.previewImageUrl && (
							<img className='biz-segment-module__preview-image' src={expandable.previewImageUrl} alt='' />
						)}
						{name && <h4 className='biz-segment-module__name'>{name}</h4>}
						{expandable.previewAsButton ? (
							<div className='biz-segment-module__preview-button'>
								<p className='biz-segment-module__preview-text'>{expandable.previewText}</p>
								<i className={`far ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
							</div>
						) : (
							!isExpanded &&
							expandable.previewText && <p className='biz-segment-module__preview-text'>{expandable.previewText}</p>
						)}
					</div>
				)}

				{isExpanded && (
					<>
						{module.children.map((child) => (
							<ModuleRenderer key={child.guid} module={child} />
						))}
					</>
				)}

				{showExpandButton && (
					<div className='biz-segment-module__expand-button-wrapper'>
						<button className='biz-btn biz-primary biz-segment-module__expand-button' onClick={handleToggle}>
							{isExpanded
								? t('GLOBAL.SHOW_LESS', { defaultValue: 'Show less' })
								: t('GLOBAL.SHOW_MORE', { defaultValue: 'Show more' })}
						</button>
					</div>
				)}
			</div>
		</ImportedGroupContext.Provider>
	);
};

export default SegmentModule;
